import { Inject, Injectable } from '@angular/core';
import { JSreport } from '../model/jsreport';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { ConfigService } from './config.service';
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { FieldConfig } from '../model/field-interface';
import { BasicResponse } from '../model/basic-response';
import { enumReportType } from '../model/types/report-type.enum';
import { ReportSummaryField } from '../model/report-summary-field';

export interface ReportAPI {
  reportDefinitions: [
    {
      groupName: string;
      reports: [Report];
    }
  ];
}

export interface Report {
  id: number;
  reportName: string;
  description: string;
  shortId: string;
  reportTemplateName: string;
  jobOnServer: boolean;
  reportPermission: string;
  reportSQL: string;
  recordDate: string;
  recordTime: string;
  reportType: enumReportType;
  reportFormatsAllowed: [string];
  defaultReportFormat: string;
  filterAllowedGIDAccess: boolean;
  reportActive: boolean;
  group?: ReportGroup;
  groupName: string;
  inputFields?: FieldConfig[];
  summaryFields?: ReportSummaryField[];
  landscape: boolean;
  uacUser: string;
  scheduleCount: number;
}

export interface ReportHistory {
  id: number;
  reportName: string;
  reportId: number;
  description: string;
  shortId: string;
  jobOnServer: boolean;
  reportSQL: string;
  reportPermission: string;
  groupName: string;
  reportType: enumReportType;
  recordDate: string;
  recordTime: string;
  requestedDate: string;
  requestedTime: string;
  completionDate: string;
  completionTime: string;
  error: string;
  username: string;
  reportFilter: string;
  reportFormat: string;
  reportBlob: string;
}

export interface ReportSchedule {
  id: number;
  reportName: string;
  reportFormat: string;
  timePeriod: string;
  timePeriodEvery: string;
  timePeriodDay: string;
  dailyFrequency: string;
  dailyFrequencyTime: string;
  dailyIncrement: string;
  dailyStartTime: string;
  dailyEndTime: string;
  runAfterGUID: string;
  lastFinished: string;
  lastSchedule: string;
  lastStarted: string;
  lastResult: string;
  emailOnCompletion: string;
  emailOnError: string;
  status: string;
  recordDate: string;
  recordTime: string;
  report?: {
    reportName?: string;
    reportId?: number;
    description?: string;
    reportFormat?: string;
  };
}
export interface ReportScheduleDBAPI {
  total_count: number;
  items: ReportSchedule[];
}

export interface ReportGroup {
  id: number;
  groupName: string;
}

export interface GroupAPI {
  groups: ReportGroup[];
}

export interface ReportTemplate {
  shortId: string;
  templateName: string;
}

export interface ReportTemplateAPI {
  templates: ReportTemplate[];
}

export interface ReportDBAPI {
  total_count: number;
  items: Report[];
}

export interface ReportHistoryDBAPI {
  total_count: number;
  items: ReportHistory[];
}

export interface SPSInfo {
  gidList: string;
}

export interface ReportPermission {
  id: number;
  name: string;
}

export interface PermissionAPI {
  permissions: ReportPermission[];
}

export interface RequestReportResponse {
  reportHistoryId: number;
}

@Injectable({
  providedIn: 'root',
})
export class ReportService {
  reportJSURL: string;
  response = '';
  askReport$ = new BehaviorSubject<JSreport>({ template: {} });
  askJSReport: JSreport = { template: {} };
  headers: HttpHeaders = new HttpHeaders().set('DoNotIntercept', 'true');

  constructor(
    @Inject(ConfigService) private config: any,
    private _httpService: HttpClient
  ) {
    this.reportJSURL =
      config.REST_SERVER_HOST +
      config.REST_SERVER_url.find((restUrl: any) => restUrl.api === 'PADM').url;
  }

  pingReportJS(): Observable<string> {
    const URL = this.reportJSURL + '/api/ping';
    return this._httpService
      .get(URL, { responseType: 'text' })
      .pipe(map((resp) => resp.toString()));
  }

  getReports(): Observable<ReportAPI> {
    const URL = this.reportJSURL + '/reports/reportlist';
    const headers = this.headers;
    return this._httpService
      .get<ReportAPI>(`${URL}`, { headers })
      .pipe(map((resp) => resp));
  }

  getGroups(): Observable<GroupAPI> {
    const URL = this.reportJSURL + '/reports/grouplist';

    return this._httpService.get<GroupAPI>(`${URL}`).pipe(map((resp) => resp));
  }

  addGroup(group: ReportGroup): Observable<BasicResponse> {
    const URL = this.reportJSURL + '/reports/addgroup';

    return this._httpService
      .put<BasicResponse>(`${URL}`, group)
      .pipe(map((resp) => resp));
  }

  updateGroup(group: ReportGroup): Observable<BasicResponse> {
    const URL = this.reportJSURL + '/reports/updategroup';

    return this._httpService
      .post<BasicResponse>(`${URL}`, group)
      .pipe(map((resp) => resp));
  }

  getTemplates(): Observable<ReportTemplateAPI> {
    const URL = this.reportJSURL + '/reports/templatelist';

    return this._httpService
      .get<ReportTemplateAPI>(`${URL}`)
      .pipe(map((resp) => resp));
  }

  addTemplate(template: ReportTemplate): Observable<BasicResponse> {
    const URL = this.reportJSURL + '/reports/addtemplate';

    return this._httpService
      .put<BasicResponse>(`${URL}`, template)
      .pipe(map((resp) => resp));
  }

  updateTemplate(
    template: ReportTemplate,
    selectedShortId: string
  ): Observable<BasicResponse> {
    const URL = this.reportJSURL + '/reports/updatetemplate/';

    return this._httpService
      .post<BasicResponse>(`${URL}${selectedShortId}`, template)
      .pipe(map((resp) => resp));
  }

  getPermissions(): Observable<PermissionAPI> {
    const URL = this.reportJSURL + '/reports/permissionlist';

    return this._httpService
      .get<PermissionAPI>(`${URL}`)
      .pipe(map((resp) => resp));
  }

  requestReport(
    reportApi: JSreport
  ): Observable<RequestReportResponse | BasicResponse> {
    const URL = this.reportJSURL + '/reports/requestReport';
    return this._httpService
      .post<RequestReportResponse | BasicResponse>(`${URL}`, reportApi)
      .pipe(
        map((resp) => resp),
        catchError(ReportService.handleError)
      );
  }

  pollReport(reportHistoryId: number): Observable<RequestReportResponse | any> {
    const URL = this.reportJSURL + '/reports/renderReport';
    return this._httpService
      .get<RequestReportResponse | any>(`${URL}/${reportHistoryId}`, {
        responseType: 'blob' as 'json',
      })
      .pipe(
        map((resp) => resp),
        catchError(this.parseErrorBlob)
      );
  }

  downloadReport(id: number): Observable<any> {
    const URL = this.reportJSURL + '/reports/history/download';
    return this._httpService
      .post(`${URL}/${id}`, '', { responseType: 'blob' as 'json' })
      .pipe(map((resp) => resp));
  }

  getReportDataTabel(
    pageIndex: number,
    pageSize: number,
    sortActive: string,
    sort: string
  ): Observable<ReportDBAPI> {
    const URL = this.reportJSURL + '/reports/reportDBList';
    const headers = this.headers;
    if (sortActive === '' || sort === '') {
      sortActive = '0';
      sort = 'asc';
    }
    return this._httpService
      .get<ReportDBAPI>(
        `${URL}/${pageIndex}/${pageSize}/${sortActive}/${sort}`,
        { headers }
      )
      .pipe(map((resp) => resp));
  }

  getReportHistoryDataTabel(
    pageIndex: number,
    pageSize: number,
    sortActive: string,
    sort: string,
    reportId = 0,
    selectedGIDs: string
  ): Observable<ReportHistoryDBAPI> {
    const URL = this.reportJSURL + '/reports/history';
    const headers = this.headers;
    // const body: SPSInfo = {gidList: selectedGIDs};
    const body = '{"gidList": ""}';

    if (sortActive === '' || sort === '') {
      sortActive = '0';
      sort = 'asc';
    }
    return this._httpService
      .post<ReportHistoryDBAPI>(
        `${URL}/${pageIndex}/${pageSize}/${sortActive}/${sort}/${reportId}`,
        body,
        { headers }
      )
      .pipe(map((resp) => resp));
  }

  getReportHistoryAllDataTable(
    pageIndex: number,
    pageSize: number,
    sortActive: string,
    sort: string,
    reportId = 0
  ): Observable<ReportHistoryDBAPI> {
    const URL = this.reportJSURL + '/reports/reporthistory';

    if (sortActive === '' || sort === '') {
      sortActive = '0';
      sort = 'asc';
    }
    return this._httpService
      .post<ReportHistoryDBAPI>(
        `${URL}/${pageIndex}/${pageSize}/${sortActive}/${sort}/${reportId}`,
        ''
      )
      .pipe(map((resp) => resp));
  }

  getReportScheduleDataTable(
    pageIndex: number,
    pageSize: number,
    sortActive: string,
    sort: string,
    reportId = 0
  ): Observable<ReportScheduleDBAPI> {
    const URL = this.reportJSURL + '/reports/schedule';
    const headers = this.headers;
    // const body: SPSInfo = {gidList: selectedGIDs};
    const body = '';

    if (sortActive === '' || sort === '') {
      sortActive = '0';
      sort = 'asc';
    }
    return this._httpService
      .post<ReportScheduleDBAPI>(
        `${URL}/${pageIndex}/${pageSize}/${sortActive}/${sort}/${reportId}`,
        body,
        { headers }
      )
      .pipe(map((resp) => resp));
  }

  saveReport(reportObj: Report): Observable<BasicResponse> {
    const URL = this.reportJSURL + '/reports/save';
    const headers = this.headers;
    console.log('saveReport: ' + reportObj);
    return this._httpService
      .post<BasicResponse>(`${URL}`, reportObj, { headers })
      .pipe(catchError(ReportService.handleError));
  }

  deleteReport(reportObj: Report): Observable<BasicResponse> {
    const URL = this.reportJSURL + '/reports/delete';
    console.log('delete Report: ' + reportObj);
    return this._httpService
      .post<BasicResponse>(`${URL}`, reportObj)
      .pipe(catchError(ReportService.handleError));
  }

  saveSchedule(scheduleObj: ReportSchedule): Observable<BasicResponse> {
    const URL = this.reportJSURL + '/reports/schedule/save';
    const headers = this.headers;
    console.log('save Schedule: ' + scheduleObj);
    return this._httpService
      .post<BasicResponse>(`${URL}`, scheduleObj, { headers })
      .pipe(catchError(ReportService.handleError));
  }

  deleteSchedule(id: number): Observable<BasicResponse> {
    const URL = this.reportJSURL + '/reports/schedule';
    const headers = this.headers;
    console.log('delete Schedule: ' + id);
    return this._httpService
      .delete<BasicResponse>(`${URL}/${id}`, { headers })
      .pipe(catchError(ReportService.handleError));
  }

  async askReport(report: Report, recipe?: string, data?: {}) {
    this.askJSReport = { template: {}, data, report };
    this.askJSReport.template.shortid = report.shortId;
    this.askJSReport.template.name = report.reportName;
    this.askJSReport.template.recipe = recipe;

    this.askJSReport.template.chrome = { landscape: report.landscape };

    await this.askReport$.next(this.askJSReport);
    console.log('askReport->>');
    console.log(this.askJSReport);
    return new Promise<void>((resolve) => resolve());
  }

  private static handleError(error: HttpErrorResponse) {
    if (error.error instanceof ErrorEvent) {
      // A client-side or network error occurred. Handle it accordingly.
      console.error('An error occurred:', error.error.message);
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong,
      console.error(
        `Backend returned code ${error.status}, ` + `body was: ${error.error}`
      );
    }
    // return an observable with a user-facing error message
    // return throwError('Something bad happened; please try again later.');
    return throwError(error);
  }

  public parseErrorBlob(err: HttpErrorResponse): Observable<any> {
    const reader: FileReader = new FileReader();
    const obs = new Observable((observer: any) => {
      reader.onloadend = (e) => {
        const messageObject = JSON.parse(reader.result as string);
        console.log(messageObject);
        observer.error({
          error: {
            message: messageObject.summary,
          },
          message: messageObject.summary,
          status: err.status,
        });
        observer.complete();
      };
    });
    reader.readAsText(err.error);
    return obs;
  }

  getWithExpiry(key: string) {
    const itemStr = localStorage.getItem(key);
    // if the item doesn't exist, return null
    if (!itemStr) {
      return null;
    }
    const item = JSON.parse(itemStr);
    const now = new Date();
    // compare the expiry time of the item with the current time
    if (now.getTime() > item.expiry) {
      // If the item is expired, delete the item from storage
      // and return null
      localStorage.removeItem(key);
      return null;
    }
    return item.value;
  }

  setWithExpiry(key: string, value: any, ttl: number) {
    const now = new Date();

    // `item` is an object which contains the original value
    // as well as the time when it's supposed to expire
    let reportArray = this.getWithExpiry(key);
    console.log('reportArray', reportArray);

    if (reportArray === null) {
      reportArray = [];
      reportArray.push(value);
    } else {
      reportArray.push(value);
    }

    const item = {
      value: reportArray,
      expiry: now.getTime() + ttl,
    };
    localStorage.setItem(key, JSON.stringify(item));
  }

  removeWithKeyId(key: string, Id: number) {
    const now = new Date();
    const reportArray = this.getWithExpiry(key);

    const newReportArray = reportArray.filter(
      (reportItem: any) => reportItem.reportHistoryId !== Id
    );

    const item = {
      value: newReportArray,
      expiry: now.getTime() + 600000,
    };
    localStorage.setItem(key, JSON.stringify(item));
    // localStorage.removeItem(key);
  }
}
